import { Grid } from "@mui/material";
import { Button, Modal } from "pepsico-ds";
import { useContext, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import CheckMark from "../../assets/CheckMark Icon.jpg";
import { CampaignSetupContext } from "../../context/CampaignSetupContext";
import { GlobalConfigContext } from "../../context/GlobalConfigContext";
import { useCampaignStateHandlerHook } from "../../hooks/useCampaignStateHandlerHook";
import { paths } from "../../routes/paths";
import { FeatureToggle, useFeatureToggle } from "../../utils/featureToggle";
import { getUpdatedCampaignConfiguration } from "../campaign/campaignUtils";
import "./styles.scss";

const OptionsCard = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 0.24rem;
  gap: 0.625rem;
  border-radius: 0.48rem;
  height: 9.75rem;
  border: ${(props) => (props.isSelected ? "2px solid green" : "2px solid white")};
  box-shadow: 0 0 17px 0 #005cbc14;
  font-size: 0.58rem;
  font-weight: 700;
  margin: 0.5rem;
  position: relative;
  transition:
    border 0.2s,
    opacity 0.2s;

  &:hover {
    border: 2px solid green; // Highlight border on hover
  }

  opacity: ${(props) => (props.isNotSelected ? 0.4 : 1)};
`;

const Checkmark = styled.div`
  position: absolute;
  top: 0.5rem;
  right: 5px;
  width: 24px;
  height: 24px;
  background-image: url(${CheckMark});
  background-size: cover;
`;

const CreateCampaignModal = ({ modalOpen, setModalOpen }) => {
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const { campaignsConfiguration, campaignGroups, refetchProgramConfig } =
    useContext(GlobalConfigContext);
  const { setCurrentCampaignTypeConfig } = useContext(CampaignSetupContext);
  const { setCampaignPresets } = useCampaignStateHandlerHook();
  const [subCampaigns, setSubCampaigns] = useState([]);
  const [isGroupSelected, setIsGroupSelected] = useState(false);

  useEffect(() => {
    !campaignsConfiguration?.length && refetchProgramConfig();
  }, [campaignsConfiguration?.length, refetchProgramConfig]);
  const isBonusFlowEnabled = useFeatureToggle(FeatureToggle.isBonusFlowEnabled);

  const updatedCampaignConfiguration = useMemo(() => {
    // Get the updated campaign configuration
    const allCampaigns = getUpdatedCampaignConfiguration(
      campaignsConfiguration,
      campaignGroups
    );
    // Filter out the "Bonus" campaign type
    return allCampaigns.filter((campaign) => {
      // Filter out campaign with key 'gift_processing' or name 'Bonus'
      if (campaign.key?.toLowerCase() === "gift_processing" && isBonusFlowEnabled) {
        return false;
      }
      return true;
    });
  }, [campaignsConfiguration, campaignGroups, isBonusFlowEnabled]);

  const navigate = useNavigate();

  const toggleModal = () => {
    setModalOpen(!modalOpen);
    setSelectedCampaign(null);
  };

  const onCampaignGroupSelect = (campaign) => {
    const isCampaignGroup =
      campaign.campaigns?.length &&
      campaignGroups?.find((group) => group.key === campaign.key);
    if (isCampaignGroup) {
      setSubCampaigns(campaign.campaigns);
      setSelectedCampaign(null);
      setIsGroupSelected(true);
    } else {
      setSelectedCampaign(campaign.key);
      setIsGroupSelected(false);
      setSubCampaigns([]);
    }
  };

  const onSubCampaignSelect = (campaignKey) => {
    setSelectedCampaign(campaignKey);
  };

  const handleCreateCampaign = () => {
    const currentCampaignTypeConfiguration = campaignsConfiguration?.find(
      (campaign) => campaign?.key === selectedCampaign
    );
    if (currentCampaignTypeConfiguration?.value) {
      setCurrentCampaignTypeConfig({
        ...currentCampaignTypeConfiguration?.value,
        key: selectedCampaign,
      });
      setCampaignPresets({
        ...currentCampaignTypeConfiguration?.value,
        type: selectedCampaign,
      });
      navigate(
        `/${paths.campaigns}/${paths.create}/${currentCampaignTypeConfiguration?.value?.steps[0]?.slug || paths.generic_setup}`
      );
    }
  };

  const handleBack = () => {
    setIsGroupSelected(false);
    setSubCampaigns([]);
    setSelectedCampaign(null);
  };

  return (
    <Modal
      className="campaign-modal"
      showModal={modalOpen}
      onCloseModal={toggleModal}
      title="Choose a campaign type"
      primaryButtonProps={{
        size: "medium",
        text: "Create Campaign",
        variant: "primary",
        onClick: handleCreateCampaign,
      }}
      secondaryButtonProps={{
        size: "medium",
        text: "Cancel",
        variant: "secondary",
        onClick: toggleModal,
      }}
      showTertiaryButton={false}
      showLink={false}
    >
      {subCampaigns.length && isGroupSelected
        ? subCampaigns?.length > 0 && (
            <>
              <div
                style={{
                  width: "100%",
                  display: "flex",
                  justifyContent: "flex-start",
                }}
              >
                <Button
                  onClick={handleBack}
                  variant="primaryInverse"
                  size="medium"
                  iconLeading="arrow_back"
                >
                  Go back
                </Button>
              </div>
              <Grid container sx={{ height: "70vh", overflowY: "scroll" }}>
                {subCampaigns?.map(
                  (campaign) =>
                    campaign.key && (
                      <Grid item xs={6} key={campaign.key}>
                        <OptionsCard
                          key={campaign.key}
                          isSelected={selectedCampaign === campaign.key}
                          isNotSelected={
                            selectedCampaign !== null &&
                            selectedCampaign !== campaign.key
                          }
                          onClick={() => onSubCampaignSelect(campaign.key)}
                        >
                          {selectedCampaign === campaign.key && <Checkmark />}
                          <img
                            src={campaign.value?.image || campaign.image}
                            alt={
                              campaign.value?.label ||
                              campaign.name ||
                              campaign.key ||
                              ""
                            }
                            className="img-fluid mb-2"
                            loading="lazy"
                            style={{
                              marginBottom: "10.06px",
                              height: "5.395625rem",
                            }}
                          />
                          <p className="text-center">
                            {campaign.value?.label ||
                              campaign.name ||
                              campaign.key ||
                              ""}
                          </p>
                        </OptionsCard>
                      </Grid>
                    )
                )}
              </Grid>
            </>
          )
        : updatedCampaignConfiguration?.length > 0 && (
            <Grid container sx={{ height: "70vh", overflowY: "scroll" }}>
              {updatedCampaignConfiguration?.map(
                (campaign) =>
                  campaign.key && (
                    <Grid item xs={6} key={campaign.key}>
                      <OptionsCard
                        key={campaign.key}
                        isSelected={selectedCampaign === campaign.key}
                        isNotSelected={
                          selectedCampaign !== null &&
                          selectedCampaign !== campaign.key
                        }
                        onClick={() => onCampaignGroupSelect(campaign)}
                      >
                        {selectedCampaign === campaign.key && <Checkmark />}
                        <img
                          src={campaign.value?.image || campaign.image}
                          alt={
                            campaign.value?.label ||
                            campaign.name ||
                            campaign.key ||
                            ""
                          }
                          className="img-fluid mb-2"
                          loading="lazy"
                          style={{ marginBottom: "10.06px", height: "5.395625rem" }}
                        />
                        <p className="text-center">
                          {campaign.value?.label ||
                            campaign.name ||
                            campaign.key ||
                            ""}
                        </p>
                      </OptionsCard>
                    </Grid>
                  )
              )}
            </Grid>
          )}
    </Modal>
  );
};

export default CreateCampaignModal;
